<script lang="ts">
import { defineComponent, PropType } from 'vue'
import TabCore from '@/components/tab/TabCore.vue'

export default defineComponent({
  name: 'TabDefault',

  components: {
    TabCore,
  },

  props: {
    items: {
      type: Object as PropType<TabItem[]>,
      required: true,
    },

    /**
     * 現在ページのタブ item key
     */
    currentTabKey: {
      type: String,
      default: '',
    },

    /**
     * タブを横幅いっぱいに並べるかどうか
     * @description true の場合、タブエリアの全体幅に対して、タブが均等幅で横いっぱいに配置される
     */
    full: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['click'],
})
</script>

<template>
  <nav class="nav">
    <TabCore
      :class="{ 'is-full': full }"
      :items="items"
      :current-tab-key="currentTabKey"
      @click="$emit('click', $event)" />
  </nav>
</template>

<style lang="sass" scoped>
/**
 * deep 修飾子で TabCore.vue にスタイルを注入
 * @see https://zenn.dev/karan_coron/articles/87597250de406018e351
 */
$tab-padding-top: 8
$tab-padding-bottom: 16
$tab-padding-horizontal: 20
$tab-padding-sp: 12
.nav
  // object
  ::v-deep(.o-list__tab)
    $root: '.o-list__tab'
    margin-bottom: changeSizePx2Em(24, $font-normal-px)
    box-shadow: $drop-bottom-shadow

    &:last-child
      margin-bottom: 0

    #{$root}--items
      min-width: 0
      display: flex
      overflow-x: auto

    &.is-full
      #{$root}--items
        justify-content: space-between

      #{$root}--item
        flex: 1

  ::v-deep(.o-label__tab)
    $root: '.o-label__tab'
    #{$root}--item
      position: relative
      line-height: 1
      color: $font-orange
      font:
        size: $font-small
        weight: bold
      text-align: center
      white-space: nowrap
      padding:
        top: changeSizePx2Em($tab-padding-top, $font-small-px)
        bottom: changeSizePx2Em($tab-padding-bottom, $font-small-px)
        left: changeSizePx2Em($tab-padding-horizontal, $font-small-px)
        right: changeSizePx2Em($tab-padding-horizontal, $font-small-px)
      cursor: default

      &::before
        content: ""
        width: 100%
        height: 2px
        display: block
        position: absolute
        bottom: 0
        left: 0
        background-color: rgba($bg-orange, 1)

    +breakpoint
      #{$root}--item
        font-size: $font-small
        padding: changeSizePx2Em($tab-padding-sp, $font-small-px)
        padding-top: 0

  ::v-deep(.o-action__tab)
    $root: '.o-action__tab'
    #{$root}--item
      width: 100%
      display: block
      position: relative
      overflow: hidden
      line-height: 1
      color: $font-black
      font-size: $font-small
      font-family: inherit  // for button element (different font families set by default) @see https://www.curict.com/item/9f/9fbb57a.html
      text-align: center
      text-decoration: none
      white-space: nowrap
      padding:
        top: changeSizePx2Em($tab-padding-top, $font-small-px)
        bottom: changeSizePx2Em($tab-padding-bottom, $font-small-px)
        left: changeSizePx2Em($tab-padding-horizontal, $font-small-px)
        right: changeSizePx2Em($tab-padding-horizontal, $font-small-px)
      +transition

      $icon-width: 16px
      $icon-height: 6px
      &::before
        content: ""
        width: 0
        height: 2px
        display: block
        position: absolute
        bottom: 0
        left: 50%
        transform: translateX(-50%)
        opacity: 0
        background-color: rgba($bg-gray, 1)
        +transition

      +mouseover
        color: $font-orange

        &::before
          width: 100%
          opacity: 1

    +breakpoint
      #{$root}--item
        font-size: $font-small
        padding: changeSizePx2Em($tab-padding-sp, $font-small-px)
        padding-top: 0
</style>
